import React, { FC, ReactNode } from 'react';
import PortalHeader from '../../assessment-portal/components/portal-header';
import {
  MOBILE_PRIMARY_CTA_CLASS,
  MOBILE_PRIMARY_CTA_ENABLED_CLASS,
} from '../../assessment-portal/components/primary-cta';

interface YoungAssessmentIntroPageProps {
  onBack: () => void;
  onNext: () => void;
  dueDateLabel?: string;
}

interface IntroPoint {
  key: string;
  tone: 'blue' | 'green' | 'amber';
  title: string;
  body: string;
  icon: ReactNode;
}

const PointIcon: FC<{ children: ReactNode }> = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {children}
  </svg>
);

// Copy from the SE-742 mockup, screen 2 ("Intro — young version").
const POINTS: IntroPoint[] = [
  {
    key: 'length',
    tone: 'blue',
    title: 'About 15 minutes, 3 short sections',
    body: 'Questions about attention, restlessness, and how things are going at school, at home and with friends.',
    icon: (
      <PointIcon>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </PointIcon>
    ),
  },
  {
    key: 'together',
    tone: 'blue',
    title: 'Do this together with a parent or carer',
    body: 'A parent, carer or family member should be with you while you answer. We’ll ask who helped on the first question.',
    icon: (
      <PointIcon>
        <circle cx="9" cy="8" r="3.2" />
        <path d="M2.8 19c0-3.2 2.8-5.2 6.2-5.2s6.2 2 6.2 5.2" />
        <path d="M17 7.5a3 3 0 0 1 0 5.6" />
        <path d="M19 19c0-2.2-1-3.8-2.6-4.6" />
      </PointIcon>
    ),
  },
  {
    key: 'save',
    tone: 'green',
    title: 'Your answers save as you go',
    body: 'Kept on this device for 15 days — no account needed.',
    icon: (
      <PointIcon>
        <path d="M5 4h10l4 4v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
        <path d="M9 13l2 2 4-4" />
      </PointIcon>
    ),
  },
  {
    key: 'honest',
    tone: 'amber',
    title: 'Answer honestly, not perfectly',
    body: 'There are no right or wrong answers.',
    icon: (
      <PointIcon>
        <path d="M21 11.5a8.5 8.5 0 0 1-12 7.7L3 21l1.8-6A8.5 8.5 0 1 1 21 11.5Z" />
      </PointIcon>
    ),
  },
];

const TIP = 'If you’re not sure about a question, answer the way things usually are — not just how today went.';
const START_LABEL = 'I’m ready — let’s go';

const YoungAssessmentIntroPage: FC<YoungAssessmentIntroPageProps> = ({
  onBack,
  onNext,
  dueDateLabel,
}) => {
  const duePrefix = dueDateLabel
    ? `Due before ${dueDateLabel}`
    : 'Due before your appointment';

  return (
    <div className="adhd-portal adhd-primer-page min-h-screen flex flex-col bg-page">
      <PortalHeader
        title="Before you start"
        subtitle="Pre-appointment questions"
        chip="15 min"
        chipTone="grey"
        progress={100}
        onBack={onBack}
      />
      <main className="flex-1 flex items-start justify-center px-4 py-6 md:py-8">
        <div className="adhd-primer-card hidden md:block">
          <div className="adhd-primer-kicker">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>
          <h1 className="adhd-primer-title">A few things before you start</h1>
          <p className="adhd-primer-sub">
            The questionnaire is the next step. Here&apos;s what to expect.
          </p>

          <div className="adhd-primer-points">
            {POINTS.map((point) => (
              <div key={point.key} className="adhd-primer-point">
                <span className="adhd-primer-pico">{point.icon}</span>
                <div>
                  <div className="adhd-primer-pt">{point.title}</div>
                  <div className="adhd-primer-pd">{point.body}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="adhd-primer-tip">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#1D3A8A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8h.01M11 12h1v4h1" />
            </svg>
            <div className="adhd-primer-tiptext">{TIP}</div>
          </div>

          <div className="adhd-primer-actions">
            <button type="button" onClick={onBack} className="adhd-primer-back">
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M19 12H5M11 18l-6-6 6-6" />
              </svg>
              Back
            </button>
            <button type="button" onClick={onNext} className="adhd-primer-start">
              {START_LABEL}
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </div>
        </div>

        <div className="adhd-primer-mobile md:hidden">
          <h1 className="adhd-primer-mobile-title">A few things before you start</h1>
          <p className="adhd-primer-mobile-lede">
            The questionnaire is the next step. Here&apos;s what to expect.
          </p>

          <div className="adhd-primer-listcard">
            {POINTS.map((point) => (
              <div key={point.key} className="adhd-primer-listrow">
                <span className={`adhd-primer-pico adhd-primer-pico--${point.tone}`}>
                  {point.icon}
                </span>
                <div>
                  <div className="adhd-primer-pt">{point.title}</div>
                  <div className="adhd-primer-pd">{point.body}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="adhd-primer-due">
            <b>{duePrefix}</b>
            {' '}
            — there&apos;s no pressure to finish in one sitting.
          </div>

          <div className="adhd-primer-tip">
            <div className="adhd-primer-tiptext">{TIP}</div>
          </div>
        </div>
      </main>

      <div className="portal-navrow-spacer md:hidden" aria-hidden />
      <div className="portal-navrow--sticky md:hidden">
        <button
          type="button"
          onClick={onNext}
          className={`${MOBILE_PRIMARY_CTA_CLASS} ${MOBILE_PRIMARY_CTA_ENABLED_CLASS}`}
        >
          {START_LABEL}
        </button>
      </div>
    </div>
  );
};

export default YoungAssessmentIntroPage;
